import { useParams, Link, Navigate } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  CheckCircle2,
  Stethoscope,
  Search,
  ListChecks,
  Clock3,
  ArrowRight,
  MessageCircle,
} from "lucide-react";
import { getProcedure, PROCEDURES } from "@/data/procedures";
import { WHATSAPP_BASE, SITE_URL } from "@/data/site";
import { Breadcrumbs, CTABand } from "@/components/Shared";
import { Seo, breadcrumbSchema, faqPageSchema } from "@/components/Seo";

export default function ProcedureDetail() {
  const { slug } = useParams();
  const proc = getProcedure(slug);

  if (!proc) return <Navigate to="/surgery-guide" replace />;

  const related = PROCEDURES.filter(
    (p) => p.slug !== proc.slug && p.category === proc.category,
  ).slice(0, 3);
  const bookText = `Hello Dr. Sanjay Teza, I would like to enquire about ${proc.short}.`;

  return (
    <div data-testid="procedure-detail-page">
      <Seo
        title={`${proc.name} in Varanasi | Dr. Sanjay Teza`}
        description={`${proc.tagline} Learn about ${proc.short} — overview, symptoms, diagnosis, procedure and recovery — by Dr. Sanjay Teza, senior ENT surgeon in Varanasi.`}
        path={`/surgery-guide/${proc.slug}`}
        schemas={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Surgery Guide", path: "/surgery-guide" },
            { name: proc.short, path: `/surgery-guide/${proc.slug}` },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "MedicalProcedure",
            name: proc.name,
            description: proc.overview,
            procedureType: "https://schema.org/SurgicalProcedure",
            bodyLocation: proc.category,
            howPerformed: proc.procedureSteps ? proc.procedureSteps.join(" ") : undefined,
            url: `${SITE_URL}/surgery-guide/${proc.slug}`,
            provider: {
              "@type": "Physician",
              name: "Dr. Sanjay Teza",
              medicalSpecialty: "Otolaryngology",
            },
          },
          ...(proc.faqs?.length ? [faqPageSchema(proc.faqs)] : []),
        ]}
      />
      <section className="border-b border-brand-line bg-white">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <Breadcrumbs
            items={[
              { label: "Home", to: "/" },
              { label: "Surgery Guide", to: "/surgery-guide" },
              { label: proc.short },
            ]}
          />
          <span className="mt-6 inline-flex items-center rounded-full bg-brand-soft px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-brand-primary">
            {proc.category}
          </span>
          <h1 className="mt-4 max-w-4xl font-heading text-3xl font-extrabold leading-tight text-brand-ink sm:text-4xl lg:text-5xl">
            {proc.name}
          </h1>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-brand-slate md:text-lg">
            {proc.tagline}
          </p>
          <a
            href={`${WHATSAPP_BASE}?text=${encodeURIComponent(bookText)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="procedure-book-cta"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-brand-primary px-6 py-3.5 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 hover:bg-brand-action"
          >
            <MessageCircle className="h-5 w-5" /> Enquire about {proc.short}
          </a>
        </div>
      </section>

      <section className="bg-brand-bg">
        <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
          {/* Overview */}
          <div className="rounded-lg border border-brand-line bg-white p-8">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-brand-soft text-brand-primary">
                <Stethoscope className="h-5 w-5" />
              </span>
              <h2 className="font-heading text-2xl font-bold text-brand-ink">Overview</h2>
            </div>
            <p className="mt-5 text-base leading-relaxed text-brand-slate">
              {proc.overview}
            </p>
          </div>

          {proc.detailed && (
            <>
              {/* Symptoms + Diagnosis */}
              <div className="mt-8 grid gap-8 md:grid-cols-2">
                <div className="rounded-lg border border-brand-line bg-white p-8">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-brand-soft text-brand-primary">
                      <ListChecks className="h-5 w-5" />
                    </span>
                    <h2 className="font-heading text-xl font-bold text-brand-ink">
                      When It's Needed
                    </h2>
                  </div>
                  <ul className="mt-5 space-y-3">
                    {proc.symptoms.map((s) => (
                      <li key={s} className="flex gap-2.5 text-sm text-brand-slate">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-action" />
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-lg border border-brand-line bg-white p-8">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-brand-soft text-brand-primary">
                      <Search className="h-5 w-5" />
                    </span>
                    <h2 className="font-heading text-xl font-bold text-brand-ink">
                      Diagnosis
                    </h2>
                  </div>
                  <p className="mt-5 text-sm leading-relaxed text-brand-slate">
                    {proc.diagnosis}
                  </p>
                </div>
              </div>

              {/* Procedure */}
              <div className="mt-8 rounded-lg border border-brand-line bg-white p-8">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-brand-soft text-brand-primary">
                    <Stethoscope className="h-5 w-5" />
                  </span>
                  <h2 className="font-heading text-xl font-bold text-brand-ink">
                    The Procedure
                  </h2>
                </div>
                <ol className="mt-5 space-y-4">
                  {proc.procedureSteps.map((step, i) => (
                    <li key={i} className="flex gap-4">
                      <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-brand-primary font-heading text-sm font-bold text-white">
                        {i + 1}
                      </span>
                      <span className="pt-0.5 text-sm leading-relaxed text-brand-slate">
                        {step}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Recovery */}
              <div className="mt-8 rounded-lg border border-brand-line bg-white p-8">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-brand-soft text-brand-primary">
                    <Clock3 className="h-5 w-5" />
                  </span>
                  <h2 className="font-heading text-xl font-bold text-brand-ink">
                    Recovery
                  </h2>
                </div>
                <p className="mt-5 text-sm leading-relaxed text-brand-slate">
                  {proc.recovery}
                </p>
                <Link
                  to="/recovery"
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-primary"
                  data-testid="procedure-recovery-link"
                >
                  Read the full recovery guide{" "}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </>
          )}

          {/* FAQs */}
          {proc.faqs?.length > 0 && (
            <div className="mt-8 rounded-lg border border-brand-line bg-white p-8">
              <h2 className="font-heading text-xl font-bold text-brand-ink">
                Frequently Asked Questions
              </h2>
              <Accordion type="single" collapsible className="mt-4" data-testid="procedure-faqs">
                {proc.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-brand-line">
                    <AccordionTrigger
                      data-testid={`procedure-faq-trigger-${i}`}
                      className="text-left font-heading text-base font-semibold text-brand-ink hover:text-brand-primary hover:no-underline"
                    >
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-brand-slate">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          {/* Related */}
          {related.length > 0 && (
            <div className="mt-12">
              <h2 className="font-heading text-xl font-bold text-brand-ink">
                Related {proc.category} Procedures
              </h2>
              <div className="mt-5 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/surgery-guide/${r.slug}`}
                    data-testid={`related-${r.slug}`}
                    className="group rounded-lg border border-brand-line bg-white p-5 transition-transform duration-200 hover:-translate-y-1 hover:border-brand-primary"
                  >
                    <h3 className="font-heading text-base font-bold text-brand-ink">
                      {r.short}
                    </h3>
                    <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-primary">
                      View <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <CTABand
        title={`Considering ${proc.short}?`}
        subtitle="Get a clear, honest assessment from Dr. Sanjay Teza before deciding on surgery."
        whatsappText={bookText}
      />
    </div>
  );
}
